import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import {
    Box,
    Chip,
    Divider,
    LinearProgress,
    Paper,
    Stack,
    Typography,
    alpha,
    useTheme
} from '@mui/material';

const ScoreChart = ({ kpiDetails }) => {
    const theme = useTheme();
    const { goals = [] } = kpiDetails;

    // Check if any scores exist across all goals
    const hasAnyScores = () => {
        return goals.some(goal => goal.scores && goal.scores.length > 0);
    };

    // Calculate average score for a goal from its scores array
    const calculateGoalAverage = (goal) => {
        if (!goal.scores || goal.scores.length === 0) return null; // Return null instead of 0
        const sum = goal.scores.reduce((acc, score) => acc + parseFloat(score.score || 0), 0);
        return sum / goal.scores.length;
    };

    // Group goals by category
    const groupGoalsByCategory = () => {
        const grouped = {};
        goals.forEach(goal => {
            const category = goal.category || 'Main';
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(goal);
        });
        return grouped;
    };

    // Calculate category average (excluding goals with no evaluations)
    const calculateCategoryAverage = (categoryGoals) => {
        const goalsWithScores = categoryGoals.filter(goal => goal.scores && goal.scores.length > 0);
        if (goalsWithScores.length === 0) return null;
        
        let totalWeightedScore = 0;
        let totalWeight = 0;
        
        goalsWithScores.forEach(goal => {
            const avgScore = calculateGoalAverage(goal);
            const weight = parseFloat(goal.weight || 0);
            if (avgScore !== null) {
                totalWeightedScore += avgScore * weight;
                totalWeight += weight;
            }
        });
        
        return totalWeight > 0 ? totalWeightedScore / totalWeight : null;
    };

    const groupedGoals = groupGoalsByCategory();

    // Calculate overall score based on weighted average (excluding goals with no scores)
    const calculateOverallScore = () => {
        if (!goals || goals.length === 0) return 0;
        const goalsWithScores = goals.filter(goal => goal.scores && goal.scores.length > 0);
        if (goalsWithScores.length === 0) return 0;
        
        let totalWeightedScore = 0;
        let totalWeight = 0;

        goalsWithScores.forEach(goal => {
            const average = calculateGoalAverage(goal);
            const weight = parseFloat(goal.weight || 0);
            if (average !== null) {
                totalWeightedScore += average * weight;
                totalWeight += weight;
            }
        });

        if (totalWeight === 0) return 0;
        const raw = totalWeightedScore / totalWeight;
        const fractional = raw - Math.floor(raw);
        return fractional >= 0.9 ? Math.ceil(raw) : raw;
    };

    const overallScore = calculateOverallScore();

    // Check if scoring has started
    const scoringStarted = hasAnyScores();

    // Calculate score distribution
    const scoreRanges = {
        failing: { min: 0, max: 0.99, color: theme.palette.error.main, label: 'Failing', rangeLabel: '0 - 1' },
        requiresImprovement: { min: 1, max: 1.99, color: theme.palette.warning.dark, label: 'Requires Improvement', rangeLabel: '1 - 2' },
        somethingAchieving: { min: 2, max: 2.99, color: theme.palette.warning.main, label: 'Something Achieving', rangeLabel: '2 - 3' },
        achieving: { min: 3, max: 3.99, color: theme.palette.info.main, label: 'Achieving', rangeLabel: '3 - 4' },
        great: { min: 4, max: 5, color: theme.palette.success.dark, label: 'Great', rangeLabel: '4 - 5' }
    };

    const getScoreRange = (score) => {
        // eslint-disable-next-line no-unused-vars
        for (const [key, range] of Object.entries(scoreRanges)) {
            if (score >= range.min && score <= range.max) {
                return range;
            }
        }
        return scoreRanges.failing;
    };

    const scoreRange = getScoreRange(overallScore);

    // Calculate goal score distribution (excluding goals with no scores)
    const goalScoreDistribution = goals.reduce((acc, goal) => {
        const averageScore = calculateGoalAverage(goal);
        if (averageScore !== null) {
            const range = getScoreRange(averageScore);
            acc[range.label] = (acc[range.label] || 0) + 1;
        }
        return acc;
    }, {});

    return (
        <Paper 
            elevation={0} 
            sx={{ 
                bgcolor: 'background.paper',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 3,
                overflow: 'hidden'
            }}
        >
            <Box sx={{ p: 3, bgcolor: 'background.paper', borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <EmojiEventsIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                    <Typography variant="h6" fontWeight={700}>
                        Score Overview
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    Performance metrics and goal analysis
                </Typography>
            </Box>

            <Box sx={{ p: 3 }}>
                {!scoringStarted ? (
                    // Show "Scoring is not started" when no scores exist
                    <Paper 
                        elevation={0}
                        sx={{ 
                            p: 6,
                            bgcolor: alpha(theme.palette.divider, 0.06),
                            border: `2px solid ${theme.palette.divider}`,
                            borderRadius: 3,
                            textAlign: 'center'
                        }}
                    >
                        <StarIcon sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2 }} />
                        <Typography variant="h5" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                            Scoring is not started
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            No scores have been assigned to any goals yet
                        </Typography>
                    </Paper>
                ) : (
                    <>
                        {/* Overall Score - Big Display */}
                        <Paper 
                            elevation={0}
                            sx={{ 
                                p: 4,
                                mb: 3,
                                bgcolor: alpha(theme.palette.divider, 0.06),
                                border: `2px solid ${alpha(scoreRange.color, 0.3)}`,
                                borderRadius: 3,
                                textAlign: 'center'
                            }}
                        >
                            <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                                <StarIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: scoreRange.color }} />
                                <Typography variant="h2" fontWeight={800} color={scoreRange.color} sx={{ fontSize: { xs: '2.75rem', sm: '3.75rem' } }}>
                                    {overallScore ? Number(overallScore).toFixed(2) : '0'}
                                </Typography>
                                <Typography variant="h5" color="text.secondary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                                    /5
                                </Typography>
                            </Box>

                            <Chip
                                label={scoreRange.label}
                                size="medium"
                                sx={{
                                    backgroundColor: scoreRange.color,
                                    color: (theme) => theme.palette.getContrastText(scoreRange.color),
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                    px: 2,
                                    mt: { xs: 1, sm: 0 }
                                }}
                            />

                            <Box sx={{ mt: 3, mx: 'auto', maxWidth: 400 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={(overallScore / 5) * 100}
                                    sx={{
                                        height: 12,
                                        borderRadius: 6,
                                        backgroundColor: alpha(scoreRange.color, 0.1),
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: scoreRange.color,
                                            borderRadius: 6,
                                            backgroundImage: `linear-gradient(90deg, ${alpha(scoreRange.color, 0.8)}, ${scoreRange.color})`
                                        }
                                    }}
                                />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {((overallScore / 5) * 100).toFixed(1)}% of maximum performance
                                </Typography>
                            </Box>
                        </Paper>

                        {/* Goal Score Distribution */}
                        <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                            Performance Distribution
                        </Typography>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                            {Object.entries(scoreRanges).map(([key, range]) => {
                                const count = goalScoreDistribution[range.label] || 0;
                                return (
                                    <Box
                                        key={key}
                                        sx={{
                                            flex: '1 1 0',
                                            minWidth: { xs: 'calc(50% - 8px)', sm: 'calc(33.333% - 11px)', md: 140 }
                                        }}
                                    >
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: { xs: 1.5, sm: 2, md: 2.5 },
                                                borderRadius: 3,
                                                bgcolor: alpha(theme.palette.divider, 0.06),
                                                border: `1px solid ${alpha(range.color, 0.3)}`,
                                                textAlign: 'center',
                                                height: '100%'
                                            }}
                                        >
                                            <Typography variant="h4" fontWeight={800} color={range.color}>
                                                {count}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                {range.label}
                                            </Typography>
                                            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                {range.rangeLabel}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Individual Goal Scores by Category */}
                        <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                            Individual Goals Performance
                        </Typography>

                        {Object.entries(groupedGoals).map(([category, categoryGoals], categoryIndex) => {
                            const categoryAverage = calculateCategoryAverage(categoryGoals);
                            
                            return (
                                <Box key={category} sx={{ mb: 4 }}>
                                    {/* Category Header */}
                                    <Box 
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'space-between',
                                            mb: 2,
                                            pb: 1.5,
                                            borderBottom: `2px solid ${theme.palette.divider}`
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={1.5}>
                                            <Typography variant="subtitle1" fontWeight={700} color="primary">
                                                {category}
                                            </Typography>
                                            <Chip
                                                label={`${categoryGoals.length} goal${categoryGoals.length !== 1 ? 's' : ''}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                            />
                                        </Box>
                                        <Box 
                                            sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: 0.5,
                                                px: 2,
                                                py: 0.75,
                                                borderRadius: 1.5
                                                // background and border removed per design
                                            }}
                                        >
                                            <Typography variant="caption" fontWeight={600} color="text.secondary">
                                                Avg:
                                            </Typography>
                                            <Typography variant="body2" fontWeight={800} color={categoryAverage !== null ? 'primary' : 'text.disabled'}>
                                                {categoryAverage !== null ? `${categoryAverage.toFixed(1)}/5` : 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Category Goals */}
                                    <Stack spacing={2}>
                                        {categoryGoals.map((goal, index) => {
                                const averageScore = calculateGoalAverage(goal);
                                const goalRange = averageScore !== null ? getScoreRange(averageScore) : null;
                        const weight = parseFloat(goal.weight || 0);
                        const weightedScore = averageScore !== null ? (averageScore * weight) / 5 : 0;

                        return (
                            <Paper
                                key={goal.id || index}
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    borderRadius: 3,
                                    bgcolor: alpha(theme.palette.divider, 0.06),
                                    border: goalRange ? `1px solid ${alpha(goalRange.color, 0.2)}` : `1px solid ${theme.palette.divider}`
                                }}
                            >
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                    <Typography variant="body1" fontWeight={600}>
                                        {goal.goal?.name || goal.name || `Goal ${index + 1}`}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        {averageScore !== null ? (
                                            <Chip
                                                label={`${averageScore.toFixed(1)}/5`}
                                                size="small"
                                                sx={{
                                                    backgroundColor: goalRange.color,
                                                    color: (theme) => theme.palette.getContrastText(goalRange.color),
                                                    fontWeight: 700
                                                }}
                                            />
                                        ) : (
                                            <Chip
                                                label="N/A"
                                                size="small"
                                                sx={{
                                                    backgroundColor: theme.palette.text.disabled,
                                                    color: theme.palette.mode === 'dark' ? '#1A1A2E' : '#fff',
                                                    fontWeight: 700
                                                }}
                                            />
                                        )}
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            {weight} weight
                                        </Typography>
                                    </Box>
                                </Box>

                                {averageScore !== null ? (
                                    <>
                                        <LinearProgress
                                            variant="determinate"
                                            value={(averageScore / 5) * 100}
                                            sx={{
                                                height: 8,
                                                borderRadius: 4,
                                                backgroundColor: alpha(goalRange.color, 0.1),
                                                '& .MuiLinearProgress-bar': {
                                                    backgroundColor: goalRange.color,
                                                    borderRadius: 4,
                                                    backgroundImage: `linear-gradient(90deg, ${alpha(goalRange.color, 0.8)}, ${goalRange.color})`
                                                }
                                            }}
                                        />
                                        
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                            <Typography variant="caption" color="text.secondary">
                                                Based on {goal.scores?.length || 0} evaluation{goal.scores?.length !== 1 ? 's' : ''}
                                            </Typography>
                                            <Typography variant="caption" fontWeight={600} color={goalRange.color}>
                                                Contributes {weightedScore.toFixed(1)} to overall
                                            </Typography>
                                        </Box>
                                    </>
                                ) : (
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                        <Typography variant="caption" color="text.secondary">
                                            No evaluations yet
                                        </Typography>
                                        <Typography variant="caption" fontWeight={600} color="text.disabled">
                                            Contributes 0.0 to overall
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                        );
                    })}
                                    </Stack>
                                </Box>
                            );
                        })}

                {/* Score Calculation Summary */}
                <Paper 
                    elevation={0}
                    sx={{ 
                        mt: 4, 
                        p: 3,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.divider, 0.06),
                        border: `1px solid ${theme.palette.divider}`
                    }}
                >
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                        Calculation Summary
                    </Typography>

                    <Stack spacing={1.5} sx={{ mb: 3 }}>
                        {goals.filter(goal => goal.scores && goal.scores.length > 0).map((goal, index) => {
                            const averageScore = calculateGoalAverage(goal);
                            const weight = parseFloat(goal.weight || 0);
                            const weightedScore = averageScore !== null ? (averageScore * weight) / 100 : 0;

                            return (
                                <Box key={goal.id || index}>
                                    <Typography variant="body2" fontWeight={600}>
                                        {goal.goal?.name || goal.name || `Goal ${index + 1}`}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                        {averageScore !== null ? `${averageScore.toFixed(1)}/5 × ${weight} weight = ${weightedScore.toFixed(1)} contribution` : 'N/A'}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ 
                        p: 3, 
                        bgcolor: alpha(theme.palette.divider, 0.06), 
                        borderRadius: 2,
                        textAlign: 'center'
                    }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            FINAL PERFORMANCE SCORE
                        </Typography>
                        <Box display="flex" alignItems="center" justifyContent="center" gap={2} mt={1}>
                            <Typography variant="h3" fontWeight={800} color="primary">
                                {((overallScore / 5) * 100).toFixed(1)}%
                            </Typography>
                            <Typography variant="h5" color="text.secondary">
                                ({Number(overallScore).toFixed(2)}/5)
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Calculated from {goals.filter(g => g.scores && g.scores.length > 0).length} goal{goals.filter(g => g.scores && g.scores.length > 0).length !== 1 ? 's' : ''} with weighted average
                        </Typography>
                    </Box>
                </Paper>
            </>
            )}
        </Box>
    </Paper>
    );
};

export default ScoreChart;